import React from "react";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {useLocale} from "i18n";
import {ContentSurface, HorizontalTableRegion, Prose, WideComparisonTable} from "components/ContentPage";
import {HostedStage} from "./TransferStages";
import {clientServerCopy} from "./copy/clientServer";

/**
 * Public, crawlable **Client-Server (hosted / relay) transfer mode** page body (`/client-server`,
 * issue ui#133).
 *
 * Dumb view: every string comes from {@link ./copy/clientServer.ts} (both locales), selected at runtime
 * by `useLocale()`, so the `/ro` SSG tree renders Romanian for free. The page is plain semantic HTML
 * styled by the shared {@link Prose} typography (tokens only — light/dark fall out for free), plus a
 * real comparison `<table>` a crawler can index. SSG-safe: no browser APIs, no portals. Internal links
 * go through the site-link seam (`nav/SiteLink`), which addresses them relative to the document inside a
 * locale mirror and root-absolute in the root tree — see `nav/sitePath.ts`.
 *
 * The comparison table sits in an `overflow-x` scroller so a narrow phone scrolls the table, never the
 * whole page.
 */
export function ClientServerContent() {
	const {locale} = useLocale();
	const {heading} = clientServerCopy[locale];
	return (
		<ContentSurface>
			<Prose>
				<h1>{heading}</h1>
				<ClientServerBody/>
			</Prose>
		</ContentSurface>
	);
}

/** Route-neutral hosted-transfer copy shared by the in-flow page and the Resources dialog. */
export function ClientServerBody() {
	const {locale} = useLocale();
	const {lede, intro, how, reliability, encryption, compare, pick} = clientServerCopy[locale];
	return (
		<>
				<p>{lede}</p>
				{intro.map((paragraph, index) => (
					<p key={index}>{paragraph}</p>
				))}

				{/* Decorative anchor: the same animated hosted stage the features dialog shows (ui#136) —
				    files hopping up to the server and back down to the recipient. Constrained + centred so
				    it reads as a hero above the crawlable copy. */}
				<div style={{maxWidth: 460, margin: "6px auto 26px"}}>
					<HostedStage/>
				</div>

				<h2>{how.heading}</h2>
				<ol>
					{how.steps.map((step, index) => (
						<li key={index}>{step}</li>
					))}
				</ol>

				<h2>{reliability.heading}</h2>
				<p>{reliability.intro}</p>
				<ul>
					{reliability.items.map((item) => (
						<li key={item.title}>
							<strong>{item.title}</strong> - {item.body}
						</li>
					))}
				</ul>

				<h2>{encryption.heading}</h2>
				<p>{encryption.body}</p>
				<p>
					{encryption.zk.before}
					<RouterLink to="/zero-knowledge">{encryption.zk.link}</RouterLink>
					{encryption.zk.after}
				</p>

				<h2>{compare.heading}</h2>
				<p>{compare.intro}</p>
				<HorizontalTableRegion role="region" tabIndex={0} aria-label={compare.heading}>
					<WideComparisonTable>
						<thead>
							<tr>
								<th scope="col">{compare.featureLabel}</th>
								<th scope="col">{compare.hostedLabel}</th>
								<th scope="col">{compare.p2pLabel}</th>
							</tr>
						</thead>
						<tbody>
							{compare.rows.map((row) => (
								<tr key={row.label}>
									<th scope="row">{row.label}</th>
									<td>{row.hosted}</td>
									<td>{row.p2p}</td>
								</tr>
							))}
						</tbody>
					</WideComparisonTable>
				</HorizontalTableRegion>

				<h2>{pick.heading}</h2>
				<p>{pick.hosted}</p>
				<p>
					{pick.p2p.before}
					<RouterLink to="/p2p">{pick.p2p.link}</RouterLink>
					{pick.p2p.after}
				</p>
				{pick.industries === null ? null : (
					<p>
						{pick.industries.lead}
						{pick.industries.links.map((link) => (
							<React.Fragment key={link.to}>
								<RouterLink to={link.to}>{link.label}</RouterLink>
								{link.tail}
							</React.Fragment>
						))}
					</p>
				)}
		</>
	);
}
